import { hexA } from "./color";

export class PlanetRenderer {
  private ctx: CanvasRenderingContext2D | null = null;
  private w = 0;
  private h = 0;
  constructor(private canvas: HTMLCanvasElement) {}

  resize(dpr: number): void {
    const r = this.canvas.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;
    this.canvas.width = Math.round(r.width * dpr);
    this.canvas.height = Math.round(r.height * dpr);
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.ctx = ctx;
    this.w = r.width;
    this.h = r.height;
  }

  draw(t: number, accent: string): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const w = this.w,
      h = this.h;
    ctx.clearRect(0, 0, w, h);
    const cx = w * 0.5,
      cy = h * 0.5;
    const R = Math.min((w * 0.5) / 1.95, (h * 0.5) / 1.42) * 0.95;
    // glow
    const g = ctx.createRadialGradient(cx, cy, R * 0.25, cx, cy, R * 1.9);
    g.addColorStop(0, hexA(accent, 0.15));
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, R * 1.9, 0, 7);
    ctx.fill();
    // halftone sphere (sweeping terminator reads as rotation)
    const s = Math.max(6, Math.round(R / 26));
    const lx = Math.cos(t * 0.5),
      ly = -0.4,
      lz = Math.sin(t * 0.5) * 0.6 + 0.6;
    const ll = Math.hypot(lx, ly, lz);
    ctx.fillStyle = accent;
    for (let y = -R; y <= R; y += s)
      for (let x = -R; x <= R; x += s) {
        if (Math.hypot(x, y) > R) continue;
        const z = Math.sqrt(Math.max(0, R * R - x * x - y * y));
        const nx = x / R,
          ny = y / R,
          nz = z / R;
        let it = (nx * lx + ny * ly + nz * lz) / ll;
        it = Math.max(0, it);
        const rad = s * 0.52 * Math.pow(it, 0.8);
        if (rad > 0.4) {
          ctx.beginPath();
          ctx.arc(cx + x, cy + y, rad, 0, 7);
          ctx.fill();
        }
      }
    // rim
    ctx.strokeStyle = hexA(accent, 0.35);
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(cx, cy, R + 3, 0, 7);
    ctx.stroke();
    // orbiting torus (purple)
    this.torus(ctx, cx, cy, R * 1.5, R * 0.36, t * 0.4, "rgba(138,123,255,0.7)");
    // orbiting satellite
    const oa = t * 0.7,
      ox = cx + Math.cos(oa) * R * 1.5,
      oy = cy + Math.sin(oa) * R * 0.5;
    ctx.fillStyle = "#8a7bff";
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1;
    ctx.fillRect(ox - 3.5, oy - 3.5, 7, 7);
    ctx.strokeRect(ox - 3.5, oy - 3.5, 7, 7);
    // sparkles
    this.spark(ctx, cx - R * 1.5, cy - R * 0.9, 8, t);
    this.spark(ctx, cx + R * 1.5, cy + R * 0.8, 6, t + 1);
    this.spark(ctx, cx - R * 0.15, cy - R * 1.25, 5, t + 2);
  }

  private spark(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, t: number): void {
    const p = 0.6 + 0.4 * Math.sin(t * 3);
    ctx.strokeStyle = `rgba(239,233,218,${0.4 * p + 0.18})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x - r, y);
    ctx.lineTo(x + r, y);
    ctx.moveTo(x, y - r);
    ctx.lineTo(x, y + r);
    ctx.stroke();
  }

  private torus(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    Rr: number,
    rr: number,
    rot: number,
    col: string,
  ): void {
    ctx.strokeStyle = col;
    ctx.lineWidth = 1;
    const NU = 22,
      NV = 10,
      tilt = 0.42;
    const pt = (u: number, v: number): [number, number] => {
      const a = (u / NU) * Math.PI * 2 + rot,
        b = (v / NV) * Math.PI * 2;
      const R2 = Rr + rr * Math.cos(b);
      const x = R2 * Math.cos(a),
        y = rr * Math.sin(b),
        z = R2 * Math.sin(a);
      const yy = y * Math.cos(tilt) - z * Math.sin(tilt);
      return [cx + x, cy + yy * 0.6];
    };
    for (let u = 0; u < NU; u++) {
      ctx.beginPath();
      for (let v = 0; v <= NV; v++) {
        const p = pt(u, v);
        v ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]);
      }
      ctx.stroke();
    }
    for (let v = 0; v < NV; v++) {
      ctx.beginPath();
      for (let u = 0; u <= NU; u++) {
        const p = pt(u, v);
        u ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]);
      }
      ctx.stroke();
    }
  }
}
